/**
 * The global frame-sampler observer (ARD-1977).
 *
 * One set of listeners on `document` sees every scroller, every layout
 * transition and every click in the app, so no component registers itself.
 * Scroll events do not bubble but do reach the document in the capture phase;
 * transition and click events bubble. The element that fired the event names
 * the episode (`describeFrameTarget`): a `data-frame-surface` marker, else
 * the tag name, nothing derived from runtime data.
 *
 * Admission first, work second. `canAdmitEpisode` is a few comparisons; only
 * when it says yes does the observer walk the DOM for a label and build a
 * sampler. At the production gap almost every event is refused, and a refused
 * event must cost nothing more than the listener call.
 *
 * One episode at a time: the first admitted signal binds the episode to its
 * element and trigger, later signals from the same element refresh it, and
 * signals from any other element are ignored until it closes. A click is the
 * exception: it TAKES OVER an open scroll or transition episode, inheriting
 * its admission rather than asking for a new one (which the production gap
 * would refuse), so the interaction the user chose is the one measured and
 * the work budget is unchanged.
 *
 * A transition episode tracks the SET of admitted properties on its element
 * (`width` and `height` may run together) and ends when that set empties. An
 * end event for a property that was never admitted (`opacity` finishing early
 * on the same element) does not close it.
 *
 * Not observable from here, and out of scope: JS-driven animations (`motion`,
 * WAAPI) emit no DOM events, and CSS keyframe animations are excluded because
 * an infinite spinner would hold the rate limit hostage.
 */
import { describeFrameTarget } from './describe-target';
import { createFrameSampler, type FrameSampler } from './frame-sampler';
import { canAdmitEpisode, tryAdmitEpisode } from './gate';
import type { FrameTrigger } from './surfaces';

export const LAYOUT_TRANSITION_PROPERTIES: ReadonlySet<string> = new Set([
  'width',
  'height',
  'min-width',
  'max-width',
  'min-height',
  'max-height',
  'flex',
  'flex-grow',
  'flex-shrink',
  'flex-basis',
  'transform',
  'translate',
  'scale',
  'grid-template-columns',
  'grid-template-rows',
  'left',
  'right',
  'top',
  'bottom',
  'inset',
  'inset-inline-start',
  'inset-inline-end',
  'margin-left',
  'margin-right',
  'margin-inline-start',
  'margin-inline-end',
]);

/** Same-label transitions closer together than this are ignored. */
export const TRANSITION_REPEAT_GAP_MS = 2_000;

/** The per-label transition clock is cleared past this many labels. */
const MAX_TRACKED_TRANSITION_LABELS = 64;

type ActiveEpisode = {
  sampler: FrameSampler;
  target: EventTarget;
  trigger: FrameTrigger;
  /** `${property}@${pseudoElement}` keys still running; transition episodes only. */
  transitions: Set<string>;
};

const transitionKey = (event: Event): string => {
  const { propertyName, pseudoElement } = event as TransitionEvent;
  return `${propertyName}@${pseudoElement ?? ''}`;
};

export function installFrameSamplerObserver(root: Document = document): () => void {
  let active: ActiveEpisode | null = null;
  const lastTransitionByLabel = new Map<string, number>();

  // Checked BEFORE admission. The sampler refuses to open on a hidden
  // document anyway, but by then the admission would already be spent and
  // the next foreground click refused for the whole gap. A background
  // download's anchor `.click()` is exactly such an event.
  const hidden = () => root.visibilityState === 'hidden';

  const current = (): ActiveEpisode | null => {
    if (active && !active.sampler.isOpen()) active = null;
    return active;
  };

  /** Open a new episode for `target`, admission already granted or inherited. */
  const start = (target: EventTarget, trigger: FrameTrigger, key?: string) => {
    const sampler = createFrameSampler(describeFrameTarget(target), trigger);
    sampler.open();
    if (!sampler.isOpen()) return;
    active = { sampler, target, trigger, transitions: new Set(key ? [key] : []) };
  };

  const onScroll = (event: Event) => {
    const target = event.target;
    if (!target) return;
    const open = current();
    if (open) {
      if (open.target === target && open.trigger === 'scroll') open.sampler.touch();
      return;
    }
    if (hidden()) return;
    const t = performance.now();
    if (!tryAdmitEpisode(t)) return;
    start(target, 'scroll');
  };

  const onClick = (event: Event) => {
    const target = event.target;
    if (!target) return;
    const open = current();
    if (open) {
      if (open.trigger === 'click') return;
      // Take over: the open episode's admission carries to the click.
      open.sampler.abort();
      active = null;
      start(target, 'click');
      return;
    }
    if (hidden()) return;
    const t = performance.now();
    if (!tryAdmitEpisode(t)) return;
    start(target, 'click');
  };

  const onTransitionStart = (event: Event) => {
    const property = (event as TransitionEvent).propertyName;
    if (!LAYOUT_TRANSITION_PROPERTIES.has(property)) return;
    const target = event.target;
    if (!target) return;

    const open = current();
    if (open) {
      // A second admitted property on the episode's own element joins it.
      if (open.target === target && open.trigger === 'transition') {
        open.transitions.add(transitionKey(event));
        open.sampler.touch();
      }
      return;
    }

    if (hidden()) return;
    const t = performance.now();
    if (!canAdmitEpisode(t)) return; // cheap; no DOM walk for a refused event

    const label = describeFrameTarget(target);
    const last = lastTransitionByLabel.get(label);
    if (last !== undefined && t - last < TRANSITION_REPEAT_GAP_MS) return;
    if (lastTransitionByLabel.size >= MAX_TRACKED_TRANSITION_LABELS)
      lastTransitionByLabel.clear();
    lastTransitionByLabel.set(label, t);

    if (!tryAdmitEpisode(t)) return;
    start(target, 'transition', transitionKey(event));
  };

  const onTransitionEnd = (event: Event) => {
    const open = current();
    if (!open || open.trigger !== 'transition' || open.target !== event.target) return;
    // Only a property this episode admitted can close it; `opacity` finishing
    // on the same element while `width` is still sliding does not.
    if (!open.transitions.delete(transitionKey(event))) return;
    if (open.transitions.size > 0) return;
    open.sampler.end();
    active = null;
  };

  root.addEventListener('scroll', onScroll, { capture: true, passive: true });
  root.addEventListener('click', onClick, { capture: true, passive: true });
  root.addEventListener('transitionstart', onTransitionStart, true);
  root.addEventListener('transitionend', onTransitionEnd, true);
  root.addEventListener('transitioncancel', onTransitionEnd, true);

  return () => {
    root.removeEventListener('scroll', onScroll, { capture: true });
    root.removeEventListener('click', onClick, { capture: true });
    root.removeEventListener('transitionstart', onTransitionStart, true);
    root.removeEventListener('transitionend', onTransitionEnd, true);
    root.removeEventListener('transitioncancel', onTransitionEnd, true);
    current()?.sampler.abort();
    active = null;
    lastTransitionByLabel.clear();
  };
}
